/**
 * The footer dock — one component, every screen.
 *
 * The board puts the same four-icon dock on the library, the shop, the profile
 * and the settings. It had been copy-pasted into each of those with its own
 * near-identical CSS, which is four places to fix a spacing change and four
 * chances for them to drift apart. One component, one stylesheet block, and the
 * current screen is a prop rather than a duplicated `is-on` class.
 *
 * «Видео» had been drawn disabled, on the reasoning that rewarded video has no
 * system behind it. That was wrong twice over: Figma draws the icon live, and
 * it draws what tapping it does — «ADDS», a pop-up saying there is no ad right
 * now. So the button is a button, it opens that pop-up, and the dock owns the
 * pop-up because the dock is on every screen and the answer is the same on all
 * of them.
 */
import { useState } from 'react';
import { Dialog } from './Dialog.js';

export type NavTab = 'shop' | 'video' | 'profile' | 'settings';

const LABELS: Record<'ru' | 'en', Record<NavTab, string>> = {
  ru: { shop: 'Магазин', video: 'Видео', profile: 'Профиль', settings: 'Настройки' },
  en: { shop: 'Shop', video: 'Video', profile: 'Profile', settings: 'Settings' },
};

export function BottomNav({
  current,
  lang,
  onShop,
  onProfile,
  onSettings,
}: {
  /** Which tab this screen is, so the dock can mark it. Omit on the library. */
  current?: NavTab;
  lang: 'ru' | 'en';
  onShop: () => void;
  onProfile: () => void;
  onSettings: () => void;
}) {
  const [adsOpen, setAdsOpen] = useState(false);
  const T = LABELS[lang];
  const cls = (tab: NavTab) => `nav-btn${current === tab ? ' here' : ''}`;

  return (
    <>
      <nav className="lib-foot" aria-label={lang === 'ru' ? 'Разделы' : 'Sections'}>
        <button className={cls('shop')} onClick={onShop} aria-label={T.shop}
          {...(current === 'shop' ? { 'aria-current': 'page' as const } : {})}>
          <span className="nav-ico ico-cart" />
        </button>
        <button className="nav-btn is-video" onClick={() => setAdsOpen(true)} aria-label={T.video}>
          <span className="nav-ico ico-play" />
          {/* The coin pair sits on the icon's lower-right in every frame, «+»
              on its shoulder: watching an ad is what pays out. */}
          <span className="nav-coin" aria-hidden="true">+</span>
        </button>
        <button className={cls('profile')} onClick={onProfile} aria-label={T.profile}
          {...(current === 'profile' ? { 'aria-current': 'page' as const } : {})}>
          <span className="nav-ico ico-profile" />
        </button>
        <button className={cls('settings')} onClick={onSettings} aria-label={T.settings}
          {...(current === 'settings' ? { 'aria-current': 'page' as const } : {})}>
          <span className="nav-ico ico-gear" />
        </button>
      </nav>

      {adsOpen && (
        <Dialog
          size="notice"
          badge="!"
          label="Реклама"
          title="Реклама в данный момент недоступна"
          action="ОК"
          onAction={() => setAdsOpen(false)}
          onClose={() => setAdsOpen(false)}
          outside={
            <>
              <span className="dlg-chibi" aria-hidden="true" />
              <p className="dlg-sub">Пожалуйста, попробуйте позже</p>
            </>
          }
        />
      )}
    </>
  );
}
